import {
  Badge, Button, Container, Group, Pagination, Stack,
  Table, Text, Title, Tooltip,
} from '@mantine/core';
import { IconFilterOff, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  accountInsuranceExportHref, type AccountInsuranceListParams,
} from '../api/accountInsurance';
import type { LifecycleStatus } from '../api/types';
import { ColumnTextFilter, hasActiveFilters } from '../components/ColumnFilters';
import {
  SortableTh, nextSort, sortParam, type SortState,
} from '../components/SortableHeader';
import { StatusExplained } from '../components/StatusExplained';
import { AccountRef, AgreementRef } from '../components/EntityRef';
import { ExportCsvButton } from '../components/ExportCsvButton';
import { NO_STATUS_SELECTED_HINT, StatusFilterPills } from '../components/StatusFilterPills';
import { useAccountInsuranceList } from '../hooks/useAccountInsurance';
import { PendingCRBadge } from '../components/PendingCRBadge';
// useDeleteAccountInsurance / useRestoreAccountInsurance / useConfirmDelete
// removed 2026-06-02 (Task #245) — destructive actions live exclusively on
// the detail page.

const PAGE_SIZE = 50;
type PolicySortKey = 'insurer' | 'policy' | 'agreement' | 'accounts' | 'status';

export function AccountInsuranceListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  // #277 polish (2026-06-08): cumulative status filter — default Active only.
  const [statusFilter, setStatusFilter] = useState<LifecycleStatus[]>(['Active']);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  // #39 (2026-06-11): four-bucket pills — zero selected = no fetch + hint.
  const hasSelection = statusFilter.length > 0 || includeDeleted;

  // ── #46 Wave 1 (2026-06-12): per-column predicate filters (C-132).
  // Replaced the old global search box.
  const [insurerQ, setInsurerQ] = useState('');
  const [policyQ, setPolicyQ] = useState('');
  const [agreementQ, setAgreementQ] = useState('');
  // #46 follow-up (2026-06-12, Pedro's catch): matches any alive Indirect
  // Account owned by the policy (bank / internal # / IBAN).
  const [accountQ, setAccountQ] = useState('');

  // P41 (2026-10-15): header sort, server-side across pages; null = by insurer.
  const [sort, setSortState] = useState<SortState<PolicySortKey> | null>(null);
  function onSort(k: PolicySortKey) {
    setSortState((s) => nextSort(s, k));
    setPage(1);
  }

  const filtersActive = hasActiveFilters({ insurerQ, policyQ, agreementQ, accountQ });
  function clearFilters() {
    setInsurerQ(''); setPolicyQ(''); setAgreementQ(''); setAccountQ('');
    setPage(1);
  }
  function paged<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  const listParams: AccountInsuranceListParams = {
    status: statusFilter.length > 0 ? statusFilter : undefined,
    include_deleted: includeDeleted,
    insurer_q: insurerQ || undefined,
    policy_q: policyQ || undefined,
    agreement_q: agreementQ || undefined,
    account_q: accountQ || undefined,
    sort: sortParam(sort),
    limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE,
  };
  const { data, isLoading, error } = useAccountInsuranceList(listParams, { enabled: hasSelection });
  // del / restore / confirmDelete removed (Task #245, 2026-06-02).
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Container size="xl">
      <Stack>
        <Group justify="space-between">
          <Title order={2}>Insurance Policies</Title>
          <Group gap="sm">
            {/* Pedro 2026-10-10: the list export, like Agreements and Persons (Admin-only). */}
            <ExportCsvButton href={accountInsuranceExportHref(listParams)} hasSelection={hasSelection} />
            <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/account-insurance/new')}>New policy</Button>
          </Group>
        </Group>
        {/* #46 (2026-06-12): the old global search box was replaced by
            the per-column header filters below (C-132). */}
        <StatusFilterPills
          entity="policy"
          statuses={statusFilter}
          onStatusesChange={(next) => { setStatusFilter(next); setPage(1); }}
          includeDeleted={includeDeleted}
          onIncludeDeletedChange={(next) => { setIncludeDeleted(next); setPage(1); }}
        />
        {error && <Text c="red">{error.detail}</Text>}

        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              {/* P41 (2026-10-15): every header sorts (server-side, across pages). */}
              <SortableTh label="Insurer" col="insurer" sort={sort} onSort={onSort} filtered={!!insurerQ} />
              <SortableTh label="Policy #" col="policy" sort={sort} onSort={onSort} filtered={!!policyQ} />
              {/* Phase A3.f (2026-06-09): parent Agreement column so users
                  can scan the policy → agreement hierarchy without opening
                  each detail page. Agreement is null for orphan/Inactive
                  policies (per accounts_ownership_chk: Active ⇔
                  agreement_id IS NOT NULL); shows em-dash in those rows. */}
              <SortableTh label="Agreement" col="agreement" sort={sort} onSort={onSort} filtered={!!agreementQ} />
              <SortableTh label="Accounts" col="accounts" sort={sort} onSort={onSort} filtered={!!accountQ} />
              <SortableTh label="Status" col="status" sort={sort} onSort={onSort} w={200} />
              {/* Actions column removed 2026-06-02 (Task #245). */}
            </Table.Tr>
            {/* #46 Wave 1 (2026-06-12): per-column filter row (C-132).
                Accounts is a derived list (no server filter); Status is
                owned by the pills. */}
            <Table.Tr>
              <Table.Th>
                <ColumnTextFilter value={insurerQ} onChange={paged(setInsurerQ)}
                  placeholder="Insurer…" ariaLabel="Filter by insurer" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={policyQ} onChange={paged(setPolicyQ)}
                  placeholder="Policy #…" ariaLabel="Filter by policy number" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={agreementQ} onChange={paged(setAgreementQ)}
                  placeholder="Code / mnemonic…" ariaLabel="Filter by agreement" />
              </Table.Th>
              <Table.Th>
                <ColumnTextFilter value={accountQ} onChange={paged(setAccountQ)}
                  placeholder="Bank / # / IBAN…" ariaLabel="Filter by linked account" />
              </Table.Th>
              <Table.Th>
                {filtersActive && (
                  <Tooltip label="Clear all column filters" withArrow>
                    <Button size="compact-xs" variant="subtle" color="gray"
                      leftSection={<IconFilterOff size={14} />}
                      onClick={clearFilters}>
                      Clear
                    </Button>
                  </Tooltip>
                )}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {/* #39: zero pills → hint instead of data (no fetch fired). */}
            {!hasSelection && (
              <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">{NO_STATUS_SELECTED_HINT}</Text></Table.Td></Table.Tr>
            )}
            {hasSelection && isLoading && <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">Loading...</Text></Table.Td></Table.Tr>}
            {/* #46 / C-132: two-tier empty state — also fixes a latent
                C-131 gating gap (this row was not hasSelection-gated). */}
            {hasSelection && !isLoading && data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  {filtersActive ? (
                    <Group gap="xs" justify="center" py="sm">
                      <Text c="dimmed" size="sm">No policies match the current filters.</Text>
                      <Button size="compact-xs" variant="subtle"
                        leftSection={<IconFilterOff size={14} />}
                        onClick={clearFilters}>
                        Clear filters
                      </Button>
                    </Group>
                  ) : (
                    <Text c="dimmed">None.</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {hasSelection && data?.items.map((a) => (
              <Table.Tr key={a.id} style={{ cursor: 'pointer', opacity: a.deleted_at ? 0.5 : 1 }}
                onClick={() => navigate(`/account-insurance/${a.id}`)}>
                <Table.Td><Text fw={500}>{a.insurer}</Text></Table.Td>
                <Table.Td><Text ff="monospace">{a.policy_number}</Text></Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <AgreementRef id={a.agreement_id} linkPathPrefix="/agreements" />
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  {/* H1.6: Policy ↔ Account is M:N. Show every linked account. */}
                  {a.account_ids.length === 0
                    ? <Text c="dimmed" size="sm">—</Text>
                    : (
                      <Stack gap={2}>
                        {a.account_ids.map((aid) => (
                          <AccountRef key={aid} id={aid} linkPathPrefix="/accounts" />
                        ))}
                      </Stack>
                    )}
                </Table.Td>
                <Table.Td>
                  <Stack gap={4} align="flex-start">
                    {/* P47: the badge explains its status on hover (same words as the pills). */}
                    <StatusExplained entity="policy" status={a.deleted_at ? 'Deleted' : a.status}>
                    {a.deleted_at
                      ? <Badge color="gray" variant="light">Deleted</Badge>
                      : <Badge
                          color={
                            a.status === 'Active' ? 'green'
                              : a.status === 'Inactive' ? 'yellow'
                              : 'gray'  // Closed
                        }
                          variant="light"
                        >{a.status}</Badge>}
                    </StatusExplained>
                    <PendingCRBadge crs={a.pending_change_requests} />
                  </Stack>
                </Table.Td>
                {/* Actions column removed (Task #245 2026-06-02). */}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {data && data.total > PAGE_SIZE && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{data.total} total</Text>
            <Pagination total={totalPages} value={page} onChange={setPage} />
          </Group>
        )}
      </Stack>
    </Container>
  );
}
